/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : authSlice.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for Auth state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 12, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 12, 2020
 * Description   : Add code to maintain state for Auth data
 *
 *
 ******************************************************************************/

import axios from 'axios';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'stores/GlobalStore';
import { getUserRoleAPI } from 'api/Oauth-API';
import { getToken } from 'api/OauthPKCE-API';
import {
  EnumTokenType,
  IAuthState,
  ITokenState,
  IUserInfoState,
  IGetAuthTokenRequestArgs,
  IGetRefreshTokenRequestArgs,
  IUserRoleState,
} from 'models/auth';
import { IAppConfig, decodeToken, getCodeChallenge, getLoginURL, appConfig } from 'shared/utils';

interface ITokenReceivedPayload {
  token: ITokenState;
  userInfo: IUserInfoState;
}
const initialState: IAuthState = {
  token: null,
  userInfo: null,
  userRole: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    tokenReceived(authState: IAuthState, action: PayloadAction<ITokenReceivedPayload>) {
      authState.token = action.payload.token;
      authState.userInfo = action.payload.userInfo;
    },
    setUserRole(authState: IAuthState, action: PayloadAction<'agent' | 'user' | null>) {
      authState.userRole = action.payload;
    }
  },
});

export const authReducer = authSlice.reducer;

export const fetchToken = <T extends IGetAuthTokenRequestArgs | IGetRefreshTokenRequestArgs>(
  args: T
): AppThunk => async dispatch => {
  let token: ITokenState;
  let userInfo: IUserInfoState;

  delete axios.defaults.headers['Authorization'];
  delete axios.defaults.headers['user-id'];
  
    token = await getToken<T>(args);

    if(token.id_token == undefined){
     const { avatar, family_name, given_name, name, sub } = decodeToken(token.access_token) as IUserInfoState;
     userInfo = { avatar, family_name, given_name, name, sub };
     axios.defaults.headers['user-id'] = sub;
    }else{
     const { avatar, family_name, given_name, name, sub } = decodeToken(token.id_token) as IUserInfoState;
     userInfo = { avatar, family_name, given_name, name, sub };
     axios.defaults.headers['user-id'] = sub;
    }    

    axios.defaults.headers['Authorization'] = `Bearer ${token.access_token}`;
    
    const userRole: IUserRoleState = await getUserRoleAPI();
    dispatch(authSlice.actions.tokenReceived({ token, userInfo }));
    dispatch(authSlice.actions.setUserRole(userRole.role));
    dispatch(setTimeoutThunk(args.appConfig));
};

export const setTimeoutThunk = (appConfig: IAppConfig): AppThunk => (dispatch, getState) => {
  const { refresh_token, expires_in } = getState().auth.token!;
  console.log("inside settimeoutthunk");
  console.log(getState().auth.token!);
  const args = { type: EnumTokenType.Refresh, refresh_token, appConfig };

  window.setTimeout(() => dispatch(fetchToken<IGetRefreshTokenRequestArgs>(args)),(expires_in - 10) * 1000);
};

export const selectIsAuthenticated = (state: RootState) => state.auth.token !== null && state.auth.userRole !== null;
export const selectToken = (state: RootState) => state.auth.token;
export const selectUserInfo = (state: RootState) => state.auth.userInfo;
export const selectUserRole = (state: RootState) => state.auth.userRole;
